import React from 'react';
import { Box } from '@mui/material';

// Sizes are in a BASE × BASE box (a 30px side nav icon) and scale with the icon: the up
// arrow is ARROW_WIDTH × ARROW_HEIGHT in the top left, ARROW_TOP down from the top, and the
// icon is cut away GAP beyond it, so a transparent gap separates them whatever the background
const BASE = 30;
const ARROW_WIDTH = 10;
const ARROW_HEIGHT = 12;
const ARROW_TOP = 2;
const GAP = 2;
const ARROW_STROKE = 2;

// An up arrow (MUI's Upgrade without its bottom line) as an SVG path in BASE units: a stem up
// the middle, and a head across the top whose arms come down ARROW_WIDTH / 2, so a taller
// arrow has a longer stem and the same head. Its ends are inset by half a stroke.
const INSET = ARROW_STROKE / 2;
const MIDDLE = ARROW_WIDTH / 2;
const ARROW_PATH = `M${MIDDLE} ${ARROW_TOP + ARROW_HEIGHT - INSET}V${ARROW_TOP + INSET}M${INSET} ${ARROW_TOP + MIDDLE}L${MIDDLE} ${ARROW_TOP + INSET}L${ARROW_WIDTH - INSET} ${ARROW_TOP + MIDDLE}`;

// Kept in the icon's top left, at its scale, even if it isn't square
const VIEW_BOX = `0 0 ${BASE} ${BASE}`;
const ASPECT = 'xMinYMin meet';

// The cut-out: the same arrow drawn GAP thicker all round (wider strokes, longer round caps),
// so the icon is cut away in the arrow's own shape. Everything (the gradient) except it shows.
const CUTOUT = encodeURIComponent(
	`<svg xmlns="http://www.w3.org/2000/svg" viewBox="${VIEW_BOX}" preserveAspectRatio="${ASPECT}"><path d="${ARROW_PATH}" stroke="black" stroke-width="${ARROW_STROKE + GAP * 2}" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>`,
);
const MASK = `url("data:image/svg+xml,${CUTOUT}") 0 0 / 100% 100% no-repeat, linear-gradient(#000 0 0)`;

/**
 * An icon (children) with a small up arrow in its top left, sized relative to the icon, for
 * the side nav's Import section and the import pages. The icon is masked out in the arrow's
 * shape, GAP wider all round, leaving a transparent gap that follows the arrow. The side nav
 * passes `color` to its icons; it's passed on to the icon and used for the arrow.
 */
export const ImportIconBadge = ({ children, color }) => (
	<Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
		<Box
			sx={{
				display: 'inline-flex',
				mask: MASK,
				WebkitMask: MASK,
				// Shows the icon where the gradient is and the cut-out isn't
				maskComposite: 'exclude',
				WebkitMaskComposite: 'xor',
			}}
		>
			{/* Without a color (e.g. in a page header) the icon keeps its own */}
			{React.cloneElement(children, { ...(color && { color }) })}
		</Box>
		<Box
			component='svg'
			viewBox={VIEW_BOX}
			preserveAspectRatio={ASPECT}
			aria-hidden
			sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
		>
			<path
				d={ARROW_PATH}
				stroke={color ?? 'currentColor'}
				strokeWidth={ARROW_STROKE}
				strokeLinecap='round'
				strokeLinejoin='round'
				fill='none'
			/>
		</Box>
	</Box>
);
